/**
 * Cached entitlement snapshot (web).
 *
 * Mirrors the mobile SQLite cache in `apps/mobile/src/offline/cache.js`, for the
 * same reason: a failed `/billing/status` call must never downgrade a paying
 * customer to Free. The API stays authoritative for anything that mutates data,
 * so a stale snapshot only ever affects what the UI shows.
 *
 * Unlike mobile — which wipes its whole database on sign-out — the browser is
 * frequently shared, so the snapshot is stored with the Firebase UID it belongs
 * to and is ignored when that does not match the current user.
 */

const STORAGE_KEY = 'chordbookpro.subscription';

export function saveSubscription(firebaseUid, status) {
  if (!firebaseUid || !status) return;

  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ firebaseUid, cachedAt: new Date().toISOString(), status }),
    );
  } catch {
    // Storage can be unavailable (private mode, quota). The app still works —
    // it simply loses the offline fallback for this session.
  }
}

/**
 * Reads the cached snapshot for a user.
 *
 * @returns {object|null} null when absent, unreadable, or stored for someone else
 */
export function loadSubscription(firebaseUid) {
  if (!firebaseUid) return null;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const entry = JSON.parse(raw);

    // Belongs to a different account — never serve one user's plan to another.
    if (entry?.firebaseUid !== firebaseUid || !entry.status) return null;

    return { ...entry.status, cachedAt: entry.cachedAt };
  } catch {
    return null;
  }
}

export function clearSubscription() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; an unreadable store is also an unusable cache.
  }
}
